/** Parse spoken qty + name lines and fuzzy-match a catalog (materials, recipes, vendors). */

import {
  applyDimensionNumberHomophones,
  bareSpokenToken,
  collapseSpokenFractions,
  digitFormToSpokenWords,
  glueSplitDimensionDigits,
  parseSpokenQty,
  spokenNumberWordsToDigits,
} from "@/lib/manufacturing/spokenNumberWords";

export type SpokenLine = {
  qty: number;
  name: string;
  raw: string;
};

export type CatalogCandidate = {
  id: string;
  name: string;
  code?: string;
};

export type CatalogMatchStatus = "exact" | "likely" | "unmatched";

export type CatalogMatch = {
  spoken: SpokenLine;
  status: CatalogMatchStatus;
  candidate: CatalogCandidate | null;
  score: number;
};

function isQtyToken(token: string): boolean {
  return /^(?:qty|quantity)$/i.test(bareSpokenToken(token));
}

function isItemSep(token: string): boolean {
  return /^(?:item|items|of)$/i.test(bareSpokenToken(token));
}

function stemToken(token: string): string {
  if (token.length > 4 && token.endsWith("es")) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith("s") && !token.endsWith("ss")) return token.slice(0, -1);
  return token;
}

function collapseDimensionXs(value: string): string {
  let prev = "";
  let cur = value;
  while (cur !== prev) {
    prev = cur;
    cur = cur.replace(/(\d+)\s+x\s+(\d+)/g, "$1x$2");
    cur = cur.replace(/(\d+)x\s+(\d+)/g, "$1x$2");
    cur = cur.replace(/(\d+)\s+x(\d+)/g, "$1x$2");
  }
  return cur;
}

type NumberMatchForms = { digits: string; words: string };

/** Digit-canonical form used for matching and leftover name equality. */
export function normalizeCatalogName(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = collapseSpokenFractions(s);
  s = s.replace(/[""]/g, '"');
  s = s.replace(/\bpressure[\s-]*treated\b/g, "pt");
  s = s.replace(/\bby\b/g, "x");
  s = applyDimensionNumberHomophones(s);
  s = spokenNumberWordsToDigits(s);
  s = s.replace(/(\d)\s*['’](?:\s*ft)?\b/g, "$1");
  s = collapseDimensionXs(s);
  s = glueSplitDimensionDigits(s);
  s = s.replace(/[^a-z0-9/"x]+/g, " ").replace(/"/g, "").replace(/\s+/g, " ").trim();
  s = s.replace(/(\d+)\s+lights?\b/g, "$1 lite");
  s = s.replace(/\bin\s+swing\b/g, "inswing");
  s = s.replace(/\bout\s+swing\b/g, "outswing");
  return s
    .split(" ")
    .filter((t) => t.length > 0)
    .map(stemToken)
    .join(" ");
}

/** 4x6x16 is not the 8' 4x6, even when the catalog writes the length as its own number. */
function lumberSize(normalized: string): { cross: string; length: string | null } | null {
  const tokens = normalized.split(" ").filter((token) => token.length > 0);
  const full = tokens.find((token) => /^\d+x\d+x\d+$/.test(token));
  if (full) {
    const [thickness, width, length] = full.split("x");
    return { cross: `${thickness}x${width}`, length: length ?? null };
  }
  const cross = tokens.find((token) => /^\d+x\d+$/.test(token));
  if (!cross) return null;
  const length = tokens.find((token) => /^\d+$/.test(token) && Number(token) >= 6);
  return { cross, length: length ?? null };
}

function sizesConflict(spoken: string, catalog: string): boolean {
  const left = lumberSize(spoken);
  const right = lumberSize(catalog);
  if (!left || !right) return false;
  if (left.cross !== right.cross) return true;
  return Boolean(left.length && right.length && left.length !== right.length);
}

function numberMatchForms(raw: string): NumberMatchForms {
  const digits = normalizeCatalogName(raw);
  return { digits, words: digitFormToSpokenWords(digits) };
}

function splitUtterances(transcript: string): string[] {
  return transcript
    .split(/\n+|(?:\s*,\s*)|(?:\s+and\s+)/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const LEADING_FILLER =
  /^(?:then|also|plus|so|ok|okay|just|like|about|around|a|an|we|we've|weve|i|i've|ive|they|there|it|it's|its|that's|thats|got|have|had|counted|count|need|needs|needed|are|is)\s+/i;

/** Drop "we have" / "counted" so the qty does not have to be the first word. */
export function stripSpokenLeadingFiller(raw: string): string {
  let s = raw.trim();
  for (let i = 0; i < 8; i++) {
    const next = s.replace(LEADING_FILLER, "").trim();
    if (next === s) break;
    s = next;
  }
  return s;
}

function lineFromQtyAndName(qty: number, name: string, raw: string): SpokenLine | null {
  const trimmedName = name.trim().replace(/^[.,;:]+|[.,;:]+$/g, "").trim();
  if (!Number.isFinite(qty) || !trimmedName) return null;
  return { qty, name: trimmedName, raw };
}

function skipEmptyTokens(tokens: string[], start: number): number {
  let i = start;
  while (i < tokens.length && !bareSpokenToken(tokens[i] ?? "")) i += 1;
  return i;
}

function qtyMarkerIndexes(text: string): number[] {
  const out: number[] = [];
  const re = /\b(?:qty|quantity)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push(m.index);
    if (m[0].length === 0) re.lastIndex += 1;
  }
  return out;
}

/** "quantity twenty item two by four…" — qty is between quantity and item. */
function parseQuantityItemChunk(chunk: string): SpokenLine | null {
  const tokens = chunk.trim().split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length < 3 || !isQtyToken(tokens[0] ?? "")) return null;
  let i = skipEmptyTokens(tokens, 1);
  const parsed = parseSpokenQty(tokens.slice(i));
  if (!parsed) return null;
  i = skipEmptyTokens(tokens, i + parsed.consumed);
  if (!isItemSep(tokens[i] ?? "")) return null;
  const name = tokens.slice(i + 1).join(" ");
  return lineFromQtyAndName(parsed.value, name, chunk.trim());
}

function parseOneUtterance(raw: string): SpokenLine | null {
  const cleaned = stripSpokenLeadingFiller(raw);
  if (!cleaned) return null;
  const marked = parseQuantityItemChunk(cleaned);
  if (marked) return marked;
  const tokens = cleaned.split(/\s+/).filter((t) => t.length > 0);
  for (let i = 0; i < tokens.length; i++) {
    const parsed = parseSpokenQty(tokens.slice(i));
    if (parsed && i + parsed.consumed < tokens.length) {
      return lineFromQtyAndName(
        parsed.value,
        tokens.slice(i + parsed.consumed).join(" "),
        cleaned,
      );
    }
  }
  return null;
}

function parseUnmarkedTranscript(transcript: string): SpokenLine[] {
  const out: SpokenLine[] = [];
  for (const raw of splitUtterances(transcript)) {
    const line = parseOneUtterance(raw);
    if (line) out.push(line);
  }
  return out;
}

export function parseSpokenInventoryLines(transcript: string): SpokenLine[] {
  const starts = qtyMarkerIndexes(transcript);
  if (starts.length === 0) return parseUnmarkedTranscript(transcript);
  const out: SpokenLine[] = [];
  out.push(...parseUnmarkedTranscript(transcript.slice(0, starts[0] ?? 0)));
  for (let i = 0; i < starts.length; i++) {
    const chunk = transcript.slice(starts[i], starts[i + 1]);
    const marked = parseQuantityItemChunk(chunk);
    if (marked) out.push(marked);
    else out.push(...parseUnmarkedTranscript(chunk));
  }
  return out;
}

export function parseSpokenPurchaseOrder(transcript: string): {
  vendorName: string | null;
  lines: SpokenLine[];
} {
  const vendorMatch = transcript.match(
    /(?:order\s+from|purchase\s+from|from|vendor)\s+([^:\n]+?)(?=\s*[:.\-]|\s+then\s+|\s+quantity\b|\s+\d)/i,
  );
  let vendorName: string | null = null;
  let rest = transcript;
  if (vendorMatch) {
    vendorName = (vendorMatch[1] ?? "").replace(/[.,;]+$/g, "").trim() || null;
    rest = transcript.slice((vendorMatch.index ?? 0) + vendorMatch[0].length);
    if (/^\d/.test(vendorMatch[0].slice(-1))) {
      rest = `${vendorMatch[0].match(/\d.*$/)?.[0] ?? ""} ${rest}`;
    }
  }
  return { vendorName, lines: parseSpokenInventoryLines(rest || transcript) };
}

function scoreNormalized(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.includes(b) || b.includes(a)) return 0.86;
  const ta = new Set(a.split(" ").filter((t) => t.length > 0 && t !== "of" && t !== "x"));
  const tb = new Set(b.split(" ").filter((t) => t.length > 0 && t !== "of" && t !== "x"));
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter += 1;
  return inter / (ta.size + tb.size - inter);
}

/** Digit-vs-digit and spelled-vs-spelled so catalog "four" and spoken "4" both hit. */
function scoreForms(spoken: NumberMatchForms, catalog: NumberMatchForms): number {
  return Math.max(
    scoreNormalized(spoken.digits, catalog.digits),
    scoreNormalized(spoken.words, catalog.words),
  );
}

function scoreCandidate(spokenName: string, candidate: CatalogCandidate): number {
  const spoken = numberMatchForms(spokenName);
  if (!spoken.digits && !spoken.words) return 0;
  const name = numberMatchForms(candidate.name);
  let best = sizesConflict(spoken.digits, name.digits) ? 0 : scoreForms(spoken, name);
  if (candidate.code) best = Math.max(best, scoreForms(spoken, numberMatchForms(candidate.code)));
  return best;
}

export function matchSpokenLineToCatalog(
  spoken: SpokenLine,
  catalog: readonly CatalogCandidate[],
): CatalogMatch {
  let best: CatalogCandidate | null = null;
  let bestScore = 0;
  for (const row of catalog) {
    const score = scoreCandidate(spoken.name, row);
    if (score > bestScore) {
      bestScore = score;
      best = row;
    }
  }
  if (best && bestScore >= 0.99) {
    return { spoken, status: "exact", candidate: best, score: bestScore };
  }
  if (best && bestScore >= 0.45) {
    return { spoken, status: "likely", candidate: best, score: bestScore };
  }
  return { spoken, status: "unmatched", candidate: null, score: bestScore };
}

export function matchSpokenLinesToCatalog(
  lines: readonly SpokenLine[],
  catalog: readonly CatalogCandidate[],
): CatalogMatch[] {
  return lines.map((line) => matchSpokenLineToCatalog(line, catalog));
}

export function matchVendorName(
  name: string,
  vendors: readonly CatalogCandidate[],
): CatalogMatch {
  return matchSpokenLineToCatalog({ qty: 1, name, raw: name }, vendors);
}
